import { createApi } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import Cookies from "js-cookie";
import getTenantId from "../../components/Tenent";
import {
  setAccessToken,
  setRefreshToken,
  showAccessDeniedModal,
} from "../slices/authSlice";

const getStore = () => require("../store").store;

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_CBAM_API,
  withCredentials: true,
  headers: {
    accept: "application/json",
  },
});

const setUpInterceptors = (getState, dispatch) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const tenantId = getTenantId();
      const token = getState()?.auth?.accessToken;
      const refresh_token = getState()?.auth?.refreshToken;

      config.headers["x-tenant-id"] = tenantId;
      if (token) {
        config.headers["Authorization"] = `Bearer ${token}`;
        config.headers["Refresh"] = `Bearer ${refresh_token}`;
      }
      return config;
    },
    (error) => Promise.reject(error),
  );

  axiosInstance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const originalConfig = err.config;

      if (originalConfig.url !== "/core/user/login" && err.response) {
        const errorMessage = err.response?.data?.error || "";

        if (
          err.response.status === 401 &&
          errorMessage.includes("access token has expired") &&
          !originalConfig._retry
        ) {
          originalConfig._retry = true;

          const accessToken = Cookies.get("access_token");
          const refreshToken = Cookies.get("refresh_token");

          if (!refreshToken || !accessToken) {
            dispatch(setAccessToken(null));
            dispatch(setRefreshToken(null));
            return Promise.reject(err);
          }

          try {
            const tenantId = getTenantId();
            const refreshHeaders = {
              "Content-Type": "application/json",
              Authorization: `Bearer ${refreshToken}`,
              "x-tenant-id": tenantId,
            };

            const { data } = await axiosInstance.post(
              "/core/refresh",
              { access_token: accessToken, refresh_token: refreshToken },
              { headers: refreshHeaders },
            );

            dispatch(setAccessToken(data?.data?.access_token));
            dispatch(setRefreshToken(data?.data?.refresh_token));

            originalConfig.headers["Authorization"] =
              `Bearer ${data?.data?.access_token}`;
            originalConfig.headers["Refresh"] =
              `Bearer ${data?.data?.refresh_token}`;
            return axiosInstance(originalConfig);
          } catch (_error) {
            dispatch(setAccessToken(null));
            dispatch(setRefreshToken(null));
            return Promise.reject(_error);
          }
        }
      }
      return Promise.reject(err);
    },
  );
};

const axiosBaseQuery =
  () =>
  async (
    { responseType, url, method, body, params, ...requestOpts },
    { getState, dispatch },
  ) => {
    setUpInterceptors(getState, dispatch);
    try {
      const blobEndpoints = [
        "api/v1/templates/production-data",
        "api/v1/templates/supplier-info",
        "api/v1/templates/fuel-energy",
        "api/v1/templates/bom",
        "api/v1/reports/",
      ];
      const isBlobRequest = blobEndpoints.some((ep) => url.includes(ep));
      const result = await axiosInstance({
        url,
        method,
        data: body,
        params,
        headers: requestOpts.headers,
        responseType: isBlobRequest ? "blob" : "json",
      });
      return { data: result.data };
    } catch (axiosError) {
      const status = axiosError.response?.status;
      const errorData = axiosError.response?.data;

      if (status === 403 && errorData?.error === "forbidden") {
        getStore().dispatch(showAccessDeniedModal());
      }
      return {
        error: {
          status,
          data: errorData || axiosError.message,
        },
      };
    }
  };

export const CBAM_MODULE_API_REDUCER_KEY = "cbam-moduleApi";

export const cbammodule = createApi({
  reducerPath: CBAM_MODULE_API_REDUCER_KEY,
  baseQuery: axiosBaseQuery(),
  tagTypes: ["cbam-module"],
  endpoints: (builder) => ({
    getTaxonomyHierarchy: builder.query({
      query: () => ({
        url: "api/taxonomies/hierarchy/",
        method: "GET",
      }),
    }),

    downloadProductionTemplate: builder.query({
      query: () => ({
        url: "api/v1/templates/production-data",
        method: "GET",
      }),
    }),

    uploadProductionData: builder.mutation({
      query: (formData) => ({
        url: "api/v1/production-data/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    addProductionDataManually: builder.mutation({
      query: (payload) => ({
        url: "api/v1/production-data",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getProductionData: builder.query({
      query: () => ({
        url: "api/v1/production-data",
        method: "GET",
      }),
      providesTags: ["cbam-module"],
    }),

    updateCbamEligible: builder.mutation({
      query: ({ id, cbam_eligible }) => ({
        url: `api/v1/production-data/${id}/cbam-eligible`,
        method: "POST",
        body: { cbam_eligible },
      }),
      invalidatesTags: ["cbam-module"],
    }),

    downloadSupplierTemplate: builder.query({
      query: () => ({ url: "api/v1/templates/supplier-info", method: "GET" }),
    }),

    getSupplierInfo: builder.query({
      query: () => ({ url: "api/v1/supplier-info", method: "GET" }),
      providesTags: ["cbam-module"],
    }),

    uploadSupplierInfo: builder.mutation({
      query: (formData) => ({
        url: "api/v1/supplier-info/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    addSupplierInfoManually: builder.mutation({
      query: (payload) => ({
        url: "api/v1/supplier-info",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    updateSupplierInfo: builder.mutation({
      query: (payload) => ({
        url: "api/v1/supplier-info/update",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    createSupplierEngagement: builder.mutation({
      query: (payload) => ({
        url: "api/v1/supplier-engagement",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    validateSupplierEngagement: builder.mutation({
      query: (payload) => ({
        url: "api/v1/supplier-engagement/validate",
        method: "POST",
        body: payload,
      }),
    }),

    submitSupplierEngagement: builder.mutation({
      query: (formData) => ({
        url: "api/v1/supplier-engagement/submit",
        method: "POST",
        body: formData,
      }),
    }),

    reviewSupplierEngagement: builder.mutation({
      query: (payload) => ({
        url: "api/v1/supplier-engagement/review",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getSupplierEngagementProof: builder.query({
      query: (engagementId) => ({
        url: `api/v1/supplier-engagement/${engagementId}/proof`,
        method: "GET",
      }),
    }),

    // ── Fuel & Energy ──────────────────────────────────────
    downloadFuelEnergyTemplate: builder.query({
      query: () => ({ url: "api/v1/templates/fuel-energy", method: "GET" }),
    }),

    uploadFuelEnergy: builder.mutation({
      query: (formData) => ({
        url: "api/v1/fuel-energy/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    addFuelEnergyManually: builder.mutation({
      query: (payload) => ({
        url: "api/v1/fuel-energy",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getFuelEnergy: builder.query({
      query: () => ({ url: "api/v1/fuel-energy", method: "GET" }),
      providesTags: ["cbam-module"],
    }),

    updateFuelEnergyMapping: builder.mutation({
      query: ({ id, fuel_factor_id }) => ({
        url: `api/v1/fuel-energy/${id}/remap`,
        method: "PATCH",
        body: { fuel_factor_id },
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getFuelEnergyUploadStatus: builder.query({
      query: () => ({ url: "api/v1/fuel-energy/upload-status", method: "GET" }),
    }),

    // ── Production Detail ──────────────────────────────────
    getProductionDetail: builder.query({
      query: (product_id) => ({
        url: "api/v1/production-data/detail",
        method: "GET",
        params: { product_id },
      }),
      providesTags: ["cbam-module"],
    }),

    // ── Fuel Energy Allocation ─────────────────────────────
    getFuelEnergyAllocation: builder.query({
      query: (production_id) => ({
        url: `api/v1/fuel-energy/allocation/${production_id}`,
        method: "GET",
      }),
      providesTags: ["cbam-module"],
    }),

    // ── Fuel Energy Allocation Status ──────────────────────
    getFuelEnergyAllocationStatus: builder.query({
      query: (product_id) => ({
        url: `api/v1/fuel-energy/allocation/status/${product_id}`,
        method: "GET",
      }),
      providesTags: ["cbam-module"],
    }),

    // ── BOM / Precursor ────────────────────────────────────
    downloadBomTemplate: builder.query({
      query: (id) => ({
        url: `api/v1/templates/bom?product_id=${id}`,
        method: "GET",
      }),
    }),

    uploadBom: builder.mutation({
      query: (formData) => ({
        url: "api/v1/bom/upload",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getBomUploadStatus: builder.query({
      query: () => ({ url: "api/v1/bom/upload-status", method: "GET" }),
    }),

    getBom: builder.query({
      query: (production_data_id) => ({
        url: "api/v1/bom",
        method: "GET",
        params: { production_data_id },
      }),
      providesTags: ["cbam-module"],
    }),

    // ── CN Code Search ─────────────────────────────────────
    searchCnCodes: builder.query({
      query: ({ q = "", limit = 1000, id, region }) => ({
        url: "api/v1/cn-codes/search",
        method: "GET",
        params: {
          q,
          limit,
          ...(id ? { id } : {}),
          ...(region ? { region } : {}),
        },
      }),
    }),

    // ── CN Code Regions ────────────────────────────────────
    getCnCodeRegions: builder.query({
      query: () => ({ url: "api/v1/cn-codes/regions", method: "GET" }),
    }),

    // ── Upload Logs ────────────────────────────────────────
    getUploadLogs: builder.query({
      query: () => ({ url: "api/v1/upload-logs", method: "GET" }),
      providesTags: ["cbam-module"],
    }),

    // ── Fuel Energy Populate ───────────────────────────────
    populateFuelEnergy: builder.mutation({
      query: (payload) => ({
        url: "api/v1/fuel-energy/populate",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── BOM Update ─────────────────────────────────────────
    updateBom: builder.mutation({
      query: (payload) => ({
        url: "api/v1/bom/update",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Fuel Energy Allocation Manual ─────────────────────
    addFuelEnergyAllocationManually: builder.mutation({
      query: (payload) => ({
        url: "api/v1/fuel-energy/allocation/manual",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Fuel Energy Factors ────────────────────────────────
    getFuelEnergyFactors: builder.query({
      query: ({ name = "", unit = "" } = {}) => ({
        url: "api/v1/fuel-energy/factors",
        method: "GET",
        params: { name, unit },
      }),
    }),

    // ── Fuel Energy Factor Units ───────────────────────────
    getFuelEnergyFactorUnits: builder.query({
      query: ({ unit = "" } = {}) => ({
        url: "api/v1/fuel-energy/factors/units",
        method: "GET",
        params: { unit },
      }),
    }),

    // ── Fuel Energy Allocation Units ───────────────────────
    getFuelEnergyAllocationUnits: builder.query({
      query: () => ({
        url: "api/v1/fuel-energy/allocation/units",
        method: "GET",
      }),
    }),

    // ── Update Production Data ─────────────────────────────
    updateProductionData: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `api/v1/production-data/${id}/update`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Delete Production Data ─────────────────────────────
    deleteProductionData: builder.mutation({
      query: (id) => ({
        url: `api/v1/production-data/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Delete Fuel & Energy ───────────────────────────────
    deleteFuelEnergy: builder.mutation({
      query: (id) => ({
        url: `api/v1/fuel-energy/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Delete Fuel Energy Allocation ─────────────────────
    deleteFuelEnergyAllocation: builder.mutation({
      query: (id) => ({
        url: `api/v1/fuel-energy/allocation/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Delete Supplier Info ───────────────────────────────
    deleteSupplierInfo: builder.mutation({
      query: (id) => ({
        url: `api/v1/supplier-info/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["cbam-module"],
    }),

    // ── Delete BOM Rows ────────────────────────────────────
    deleteBomRows: builder.mutation({
      query: (ids) => ({
        url: "api/v1/bom/rows",
        method: "DELETE",
        body: { ids },
      }),
      invalidatesTags: ["cbam-module"],
    }),

    getDashboard: builder.query({
      query: ({ year, certificate_cost } = {}) => ({
        url: "api/v1/dashboard",
        method: "GET",
        params: {
          ...(year != null && { year }),
          ...(certificate_cost != null && { certificate_cost }),
        },
      }),
      providesTags: ["cbam-module"],
    }),

    getDashboardProductDetail: builder.query({
      query: (product_id) => ({
        url: `api/v1/dashboard/products/${product_id}`,
        method: "GET",
      }),
      providesTags: ["cbam-module"],
    }),

    getDashboardMaterials: builder.query({
      query: ({ year } = {}) => ({
        url: "api/v1/dashboard/materials",
        method: "GET",
        params: year ? { year } : {},
      }),
      providesTags: ["cbam-module"],
    }),

    getDashboardSuppliers: builder.query({
      query: ({ year } = {}) => ({
        url: "api/v1/dashboard/suppliers",
        method: "GET",
        params: year ? { year } : {},
      }),
      providesTags: ["cbam-module"],
    }),

    // ── Benchmark Sectors ──────────────────────────────────
    getBenchmarkSectors: builder.query({
      query: () => ({
        url: "api/v1/benchmarks/sectors",
        method: "GET",
      }),
    }),

    // ── Benchmarks (by sector) ─────────────────────────────
    getBenchmarks: builder.query({
      query: (sector) => ({
        url: "api/v1/benchmarks",
        method: "GET",
        params: { sector },
      }),
    }),

    // ── Download CBAM Report ───────────────────────────────
    downloadCbamReport: builder.query({
      query: (reportId) => ({
        url: `api/v1/reports/${reportId}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useGetDashboardQuery,
  useGetDashboardProductDetailQuery,
  useLazyGetDashboardProductDetailQuery,
  useGetDashboardMaterialsQuery,
  useGetDashboardSuppliersQuery,
  useGetBenchmarkSectorsQuery,
  useLazyGetBenchmarksQuery,
  useGetTaxonomyHierarchyQuery,
  useLazyDownloadProductionTemplateQuery,
  useUploadProductionDataMutation,
  useAddProductionDataManuallyMutation,
  useGetProductionDataQuery,
  useUpdateCbamEligibleMutation,
  useLazyDownloadSupplierTemplateQuery,
  useGetSupplierInfoQuery,
  useUploadSupplierInfoMutation,
  useAddSupplierInfoManuallyMutation,
  useUpdateSupplierInfoMutation,
  useCreateSupplierEngagementMutation,
  useValidateSupplierEngagementMutation,
  useSubmitSupplierEngagementMutation,
  useReviewSupplierEngagementMutation,
  useLazyGetSupplierEngagementProofQuery,
  useLazyDownloadFuelEnergyTemplateQuery,
  useUploadFuelEnergyMutation,
  useAddFuelEnergyManuallyMutation,
  useGetFuelEnergyQuery,
  useUpdateFuelEnergyMappingMutation,
  useLazyGetFuelEnergyUploadStatusQuery,
  useGetProductionDetailQuery,
  useGetFuelEnergyAllocationQuery,
  useGetFuelEnergyAllocationStatusQuery,
  useLazyDownloadBomTemplateQuery,
  useUploadBomMutation,
  useLazyGetBomUploadStatusQuery,
  useGetBomQuery,
  useLazySearchCnCodesQuery,
  useGetCnCodeRegionsQuery,
  useUpdateBomMutation,
  usePopulateFuelEnergyMutation,
  useGetUploadLogsQuery,
  useAddFuelEnergyAllocationManuallyMutation,
  useLazyGetFuelEnergyFactorsQuery,
  useGetFuelEnergyFactorUnitsQuery,
  useGetFuelEnergyAllocationUnitsQuery,
  useUpdateProductionDataMutation,
  useDeleteProductionDataMutation,
  useDeleteFuelEnergyMutation,
  useDeleteFuelEnergyAllocationMutation,
  useDeleteSupplierInfoMutation,
  useDeleteBomRowsMutation,
  useLazyDownloadCbamReportQuery,
} = cbammodule;
